import {
  ArrowClockwise,
  ChartBar,
  Check,
  DownloadSimple,
  Eye,
  EyeSlash,
  EnvelopeSimple,
  GlobeHemisphereWest,
  ImageSquare,
  Pause,
  Play,
  Prohibit,
  Sparkle,
  Trash,
  Warning,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import type { AdminAuditEntry, AdminReportAnalytics, AdminSubmission, AdminWorkflowRun } from "../../shared/types";
import {
  adminAction,
  deleteAdminSubmission,
  downloadAdminCsv,
  getAdminAudit,
  getAdminControl,
  getAdminReportAnalytics,
  getAdminSubmissions,
  getAdminWorkflows,
  replaceAdminImage,
  testAdminAlert,
  updateAdminControl,
} from "../api";

type Control = {
  phase?: string;
  hide_pictures?: number;
  hide_all_content?: number;
  active_show_id?: string | null;
};

const emptyReportAnalytics: AdminReportAnalytics = {
  total: 0,
  today: 0,
  countries: [],
  recent: [],
};

const countryName = (countryCode: string): string => {
  if (countryCode === "XX") return "Unknown country";
  if (countryCode === "T1") return "Tor network";
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode) ?? countryCode;
  } catch {
    return countryCode;
  }
};

export function AdminPage() {
  const [items, setItems] = useState<AdminSubmission[]>([]);
  const [control, setControl] = useState<Control>({});
  const [auditLog, setAuditLog] = useState<AdminAuditEntry[]>([]);
  const [workflowRuns, setWorkflowRuns] = useState<AdminWorkflowRun[]>([]);
  const [reportAnalytics, setReportAnalytics] = useState<AdminReportAnalytics>(emptyReportAnalytics);
  const [cursor, setCursor] = useState<string | null>(null);
  const [listLimit, setListLimit] = useState(50);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [overviewStats, setOverviewStats] = useState({
    approved: 0,
    inRotation: 0,
    picturesInRotation: 0,
    rejected: 0,
    technical: 0,
  });
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    try {
      const query = new URLSearchParams();
      query.set("limit", String(listLimit));
      if (status) query.set("status", status);
      if (search) query.set("q", search);
      const [list, show, audit, workflows, reportViews] = await Promise.all([
        getAdminSubmissions(`?${query}`),
        getAdminControl(),
        getAdminAudit(),
        getAdminWorkflows(),
        getAdminReportAnalytics(),
      ]);
      setItems(list.items);
      setCursor(list.cursor);
      setTotalSubmissions(list.total);
      setOverviewStats(list.stats);
      setControl(show.control as Control);
      setAuditLog(audit.items);
      setWorkflowRuns(workflows.items);
      setReportAnalytics(reportViews);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Admin could not load.");
    } finally {
      setLoading(false);
    }
  }, [listLimit, search, status]);

  const loadMore = () => {
    if (!cursor) return;
    setListLimit((current) => current + 50);
  };

  useEffect(() => {
    const timeout = window.setTimeout(load, 220);
    const refresh = () => {
      if (document.visibilityState === "visible") void load();
    };
    const interval = window.setInterval(refresh, 5_000);
    document.addEventListener("visibilitychange", refresh);

    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  const act = async (id: string, payload: Record<string, unknown>) => {
    try {
      await adminAction(id, payload);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Action failed.");
    }
  };

  const setShow = async (payload: Record<string, unknown>) => {
    try {
      const result = await updateAdminControl(payload);
      setControl(result.control as Control);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Control update failed.");
    }
  };

  const edit = async (item: AdminSubmission) => {
    const name = window.prompt("Name", item.name);
    if (name === null) return;
    const message = window.prompt("Message", item.message);
    if (message === null) return;
    await act(item.id, { action: "update_content", name, message });
  };

  const replaceImage = async (item: AdminSubmission, file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      await replaceAdminImage(item.id, file);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Replacement image failed moderation.");
    }
  };

  const sendTestAlert = async () => {
    try {
      const result = await testAdminAlert();
      setError(result.sent ? null : "Alert email is not enabled or could not be sent.");
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Alert test failed.");
    }
  };

  return (
    <main className="admin-page">
      <header className="admin-header">
        <div>
          <span className="eyebrow">Madonna Outernet / Control</span>
          <h1>Installation<br />desk.</h1>
        </div>
        <div className="admin-header__actions">
          <button className="button button--outline" type="button" onClick={() => void sendTestAlert()}>
            <EnvelopeSimple size={20} weight="bold" /> Test alert email
          </button>
          <button className="button button--outline" type="button" onClick={() => void downloadAdminCsv()}>
            <DownloadSimple size={20} weight="bold" /> Export CSV
          </button>
          <button className="button button--black" type="button" onClick={() => void setShow({ reload: true })}>
            <ArrowClockwise size={20} weight="bold" /> Reload all views
          </button>
        </div>
      </header>

      <section className="admin-control" aria-labelledby="show-control-title">
        <div className="admin-control__title">
          <span className={`status-dot status-dot--${control.phase ?? "idle"}`} />
          <div><span className="eyebrow">Current state</span><h2 id="show-control-title">{control.phase ?? "Loading"}</h2></div>
        </div>
        <div className="admin-control__buttons">
          <button type="button" onClick={() => void setShow({ phase: "qr", startNewShow: true })}><Play size={19} weight="fill" /> New show / QR</button>
          <button type="button" onClick={() => void setShow({ phase: "live" })}><Eye size={19} weight="bold" /> Go live</button>
          <button type="button" onClick={() => void setShow({ phase: "paused" })}><Pause size={19} weight="fill" /> Pause</button>
          <button className="danger" type="button" onClick={() => void setShow({ phase: "fallback" })}><Warning size={19} weight="fill" /> Branded fallback</button>
          <button type="button" onClick={() => void setShow({ hidePictures: !control.hide_pictures })}>
            {control.hide_pictures ? <Eye size={19} /> : <EyeSlash size={19} />} {control.hide_pictures ? "Show pictures" : "Hide pictures"}
          </button>
          <button className="danger" type="button" onClick={() => void setShow({ hideAllContent: !control.hide_all_content })}>
            <Prohibit size={19} weight="bold" /> {control.hide_all_content ? "Show user content" : "Hide all user content"}
          </button>
        </div>
      </section>

      <section className="admin-stats" aria-label="Submission overview">
        <div><strong>{totalSubmissions}</strong><span>Total submissions</span></div>
        <div><strong>{items.length}</strong><span>Loaded</span></div>
        <div><strong>{overviewStats.approved}</strong><span>Approved</span></div>
        <div><strong>{control.hide_all_content ? 0 : overviewStats.inRotation}</strong><span>In screen rotation</span></div>
        <div><strong>{control.hide_all_content || control.hide_pictures ? 0 : overviewStats.picturesInRotation}</strong><span>Pictures in rotation</span></div>
        <div><strong>{overviewStats.rejected}</strong><span>Rejected</span></div>
        <div><strong>{overviewStats.technical}</strong><span>Technical</span></div>
      </section>

      <section className="admin-report" aria-labelledby="report-analytics-title">
        <header className="admin-report__header">
          <div>
            <span className="eyebrow">Private report page</span>
            <h2 id="report-analytics-title"><ChartBar size={30} weight="bold" aria-hidden="true" /> Page openings</h2>
            <p>Country is estimated by Cloudflare. IP addresses and precise locations are not stored.</p>
          </div>
          <a className="button button--outline" href="/all-cards-report" target="_blank" rel="noreferrer">
            Open All Cards – Report
          </a>
        </header>

        <div className="admin-report__summary" aria-label="Report page view totals">
          <div><strong>{reportAnalytics.total}</strong><span>Total views</span></div>
          <div><strong>{reportAnalytics.today}</strong><span>Today (UTC)</span></div>
          <div><strong>{reportAnalytics.countries.length}</strong><span>Countries</span></div>
        </div>

        <div className="admin-report__content">
          <section className="admin-report__countries" aria-labelledby="report-countries-title">
            <h3 id="report-countries-title"><GlobeHemisphereWest size={22} weight="bold" aria-hidden="true" /> Countries</h3>
            <div className="admin-report__country-list">
              {reportAnalytics.countries.map((country) => (
                <div key={country.countryCode}>
                  <span><b>{country.countryCode}</b> {countryName(country.countryCode)}</span>
                  <strong>{country.count}</strong>
                </div>
              ))}
              {!reportAnalytics.countries.length && <p>No report page views recorded yet.</p>}
            </div>
          </section>

          <section className="admin-report__recent" aria-labelledby="report-recent-title">
            <h3 id="report-recent-title">Latest openings</h3>
            <div className="admin-report__table-wrap">
              <table>
                <thead><tr><th scope="col">Country</th><th scope="col">Opened</th></tr></thead>
                <tbody>
                  {reportAnalytics.recent.map((view) => (
                    <tr key={view.id}>
                      <td><b>{view.countryCode}</b> {countryName(view.countryCode)}</td>
                      <td><time dateTime={view.createdAt}>{new Date(view.createdAt).toLocaleString("en-GB")}</time></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!reportAnalytics.recent.length && <p>No report page views recorded yet.</p>}
            </div>
          </section>
        </div>
      </section>

      <section className="admin-operations" aria-label="Operations history">
        <details>
          <summary>Workflow history <span>{workflowRuns.length}</span></summary>
          <div className="admin-log">
            {workflowRuns.map((run) => (
              <div className="admin-log__row" key={run.id}>
                <strong className={`run-status run-status--${run.status}`}>{run.status}</strong>
                <span>{run.kind}</span>
                <code>{run.submissionId.slice(0, 8)}</code>
                <span>{run.detail ?? "—"}</span>
                <time>{new Date(run.updatedAt).toLocaleString("en-GB")}</time>
              </div>
            ))}
            {!workflowRuns.length && <p>No Workflow runs recorded yet.</p>}
          </div>
        </details>
        <details>
          <summary>Audit log <span>{auditLog.length}</span></summary>
          <div className="admin-log">
            {auditLog.map((entry) => (
              <div className="admin-log__row" key={entry.id}>
                <strong>{entry.action}</strong>
                <span>{entry.actor}</span>
                <code>{entry.submissionId?.slice(0, 8) ?? "system"}</code>
                <span>{entry.metadata ?? "—"}</span>
                <time>{new Date(entry.createdAt).toLocaleString("en-GB")}</time>
              </div>
            ))}
            {!auditLog.length && <p>No administrative actions recorded yet.</p>}
          </div>
        </details>
      </section>

      <section className="admin-feed">
        <div className="admin-filters">
          <input value={search} onChange={(event) => { setSearch(event.target.value); setListLimit(50); }} placeholder="Search name, email or message" aria-label="Search submissions" />
          <select value={status} onChange={(event) => { setStatus(event.target.value); setListLimit(50); }} aria-label="Filter by status">
            <option value="">All statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
            <option value="technical_hold">Technical hold</option>
          </select>
          <button
            className="icon-button"
            type="button"
            onClick={() => void load()}
            aria-label="Refresh now. This page refreshes automatically every 5 seconds."
            title="Auto-refreshes every 5 seconds"
          >
            <ArrowClockwise size={22} />
          </button>
        </div>

        {error && (
          <div className="admin-error" role="alert">
            <p>{error}</p>
            {error.toLowerCase().includes("access") && location.hostname === "localhost" && (
              <button type="button" onClick={() => {
                const token = window.prompt("Local ADMIN_DEV_TOKEN");
                if (token) { localStorage.setItem("madonna-admin-token", token); void load(); }
              }}>Set local token</button>
            )}
          </div>
        )}

        <div className="admin-grid">
          {items.map((item) => (
            <article className={`admin-card admin-card--${item.status}`} key={item.id}>
              <div className="admin-card__top">
                <span className="admin-card__status">{item.status.replace("_", " ")}</span>
                <time>{new Date(item.createdAt).toLocaleString("en-GB")}</time>
              </div>
              {item.hasGenerated && item.photoStatus === "ready" && (
                <img src={`/api/admin/media/${item.id}`} alt="Generated portrait" />
              )}
              {item.hasOriginal && (
                <details className="admin-card__original">
                  <summary>Reveal secured original</summary>
                  <img loading="lazy" src={`/api/admin/original/${item.id}`} alt="Original camera portrait" />
                  <p>Admin-only source. It is never sent to public screens.</p>
                </details>
              )}
              <div className="admin-card__body">
                <h3>{item.name}</h3>
                <p>{item.message}</p>
                <dl>
                  <div><dt>Email</dt><dd>{item.email ?? "—"}</dd></div>
                  <div><dt>Photo</dt><dd>{item.photoStatus}</dd></div>
                  <div><dt>AI</dt><dd>{item.model ?? "—"}</dd></div>
                  <div><dt>Reason</dt><dd>{item.moderationReason ?? "—"}</dd></div>
                </dl>
              </div>
              <div className="admin-card__actions">
                <button type="button" onClick={() => void act(item.id, { action: "toggle", enabled: !item.displayEnabled })} disabled={item.status !== "approved"}>
                  {item.displayEnabled ? <EyeSlash size={18} /> : <Eye size={18} />} {item.displayEnabled ? "Disable" : "Enable"}
                </button>
                <button type="button" onClick={() => void act(item.id, { action: "feature", featured: !item.featured })}>
                  <Sparkle size={18} weight={item.featured ? "fill" : "regular"} /> {item.featured ? "Unfeature" : "Show more"}
                </button>
                <button type="button" onClick={() => void act(item.id, { action: "approve_text_only" })}><Check size={18} weight="bold" /> Text only</button>
                <button type="button" onClick={() => void act(item.id, { action: "retry" })}><ArrowClockwise size={18} /> Retry AI</button>
                {item.hasOriginal && item.photoStatus !== "rejected" && (
                  <button type="button" onClick={() => void act(item.id, { action: "regenerate" })}><Sparkle size={18} /> Regenerate</button>
                )}
                <label className="admin-upload">
                  <ImageSquare size={18} /> Replace image
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(event) => {
                      void replaceImage(item, event.target.files?.[0]);
                      event.target.value = "";
                    }}
                  />
                </label>
                <button type="button" onClick={() => void edit(item)}>Edit</button>
                <button className="danger" type="button" onClick={() => void act(item.id, { action: "reject" })}><Prohibit size={18} /> Reject</button>
                <button className="danger" type="button" onClick={async () => {
                  if (window.confirm("Permanently delete this submission and its media?")) { await deleteAdminSubmission(item.id); await load(); }
                }}><Trash size={18} /> Delete</button>
              </div>
            </article>
          ))}
        </div>
        {!loading && !items.length && <p className="admin-empty">No submissions match this view.</p>}
        {loading && <p className="admin-empty">Loading installation data…</p>}
        {cursor && (
          <button className="button button--black admin-load-more" type="button" onClick={loadMore}>
            Load more submissions
          </button>
        )}
      </section>
    </main>
  );
}
